import React from 'react';
import { Select } from 'antd';

// Shared low-level UI primitives + style helpers for the journal day-detail screen.
export const C = {
  // Accents
  white: '#fff',
  blue: '#3A7DFF',
  blueTx: '#2A5FCC',
  blueDark: '#1D3F7A',
  gold: '#C99A3F',
  goldDark: '#A8842F',
  green: '#39A567',
  greenTx: '#268354',
  red: '#E0655D',
  redTx: '#C04A43',
  // Surfaces & text
  surface: 'var(--jd-surface)',
  surface2: 'var(--jd-surface-2)',
  surface3: 'var(--jd-surface-3)',
  surface4: 'var(--jd-surface-4)',
  ink: 'var(--jd-ink)',
  text: 'var(--jd-text)',
  title: 'var(--jd-title)',
  muted: 'var(--jd-muted)',
  faint: 'var(--jd-faint)',
  border: 'var(--jd-border)',
  borderSoft: 'var(--jd-border-soft)',
  divider: 'var(--jd-divider)',
  // Tonal tints (theme-aware): soft bg + border per state
  greenBg: 'var(--jd-green-bg)',
  greenBd: 'var(--jd-green-bd)',
  goldBg: 'var(--jd-gold-bg)',
  goldBd: 'var(--jd-gold-bd)',
  redBg: 'var(--jd-red-bg)',
  redBd: 'var(--jd-red-bd)',
  warnBg: 'var(--jd-warn-bg)',
  warnBd: 'var(--jd-warn-bd)',
  // Neutral greys (theme-aware)
  track: 'var(--jd-track)',
  onMuted: 'var(--jd-on-muted)',
  hint: 'var(--jd-hint)',
  placeholder: 'var(--jd-placeholder)',
  chipOffBd: 'var(--jd-chip-off-bd)',
  chipOffTx: 'var(--jd-chip-off-tx)',
  checkOffBd: 'var(--jd-check-off-bd)',
  numFaint: 'var(--jd-num-faint)',
  labelStrong: 'var(--jd-label-strong)',
  iconBtnBd: 'var(--jd-icon-btn-bd)',
  addDashedBd: 'var(--jd-add-dashed-bd)',
  // Warm gold badge / pill
  goldPillBg: 'var(--jd-gold-pill-bg)',
  goldPillBd: 'var(--jd-gold-pill-bd)',
  goldStageBg: 'var(--jd-gold-stage-bg)',
  goldStageTx: 'var(--jd-gold-stage-tx)',
  // Info (blue) status
  infoBg: 'var(--jd-info-bg)',
  infoBd: 'var(--jd-info-bd)',
  infoTx: 'var(--jd-info-tx)',
  infoAc: 'var(--jd-info-ac)',
  // Success status
  okBg: 'var(--jd-ok-bg)',
  okBd: 'var(--jd-ok-bd)',
  okTx: 'var(--jd-ok-tx)',
  // Future / disabled status
  futureBd: 'var(--jd-future-bd)',
  futureTx: 'var(--jd-future-tx)',
  futureAc: 'var(--jd-future-ac)',
  stepperActiveBg: 'var(--jd-stepper-active-bg)',
  uppercaseFaint: 'var(--jd-uppercase-faint)',
  // Neutral grey chip / "wait" segment
  neutralBg: 'var(--jd-neutral-bg)',
  neutralBd: 'var(--jd-neutral-bd)',
  neutralTx: 'var(--jd-neutral-tx)',
  // Soft success / warn verdict callouts
  okSoftBg: 'var(--jd-ok-soft-bg)',
  okSoftBd: 'var(--jd-ok-soft-bd)',
  okIconBg: 'var(--jd-ok-icon-bg)',
  warnIconBg: 'var(--jd-warn-icon-bg)',
  cancelBg: 'var(--jd-cancel-bg)',
  // Purple scenario palette
  purpleAc: 'var(--jd-purple-ac)',
  purpleBg: 'var(--jd-purple-bg)',
  purpleBd: 'var(--jd-purple-bd)',
  purpleTx: 'var(--jd-purple-tx)',
  // Misc structural greys
  rowDivider: 'var(--jd-row-divider)',
  dashedSoft: 'var(--jd-dashed-soft)',
  trackSoft: 'var(--jd-track-soft)',
  imgBg: 'var(--jd-img-bg)',
};

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase', color: C.faint, marginBottom: 11 }}>
      {children}
    </div>
  );
}

export function FieldLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ fontSize: 12.5, fontWeight: 600, color: C.muted, marginBottom: 0, ...style }}>{children}</div>;
}

const inputBase: React.CSSProperties = {
  width: '100%',
  padding: '12px 13px',
  border: `1px solid ${C.border}`,
  borderRadius: 11,
  fontSize: 14,
  color: C.text,
  outline: 'none',
  fontFamily: 'inherit',
  background: C.surface,
};

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      style={{ ...inputBase, ...props.style }}
      onFocus={(e) => { e.currentTarget.style.borderColor = C.gold; props.onFocus?.(e); }}
      onBlur={(e) => { e.currentTarget.style.borderColor = C.border; props.onBlur?.(e); }}
    />
  );
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      style={{ ...inputBase, minHeight: 54, lineHeight: 1.6, resize: 'vertical', ...props.style }}
      onFocus={(e) => { e.currentTarget.style.borderColor = C.gold; props.onFocus?.(e); }}
      onBlur={(e) => { e.currentTarget.style.borderColor = C.border; props.onBlur?.(e); }}
    />
  );
}

// Rounded "pill" toggle button (BUY / SELL / neutral tones)
export type PillTone = 'buy' | 'sell' | 'neutral';
export function Pill({ active, tone, children, onClick }: { active: boolean; tone: PillTone; children: React.ReactNode; onClick: () => void }) {
  const c = tone === 'buy' ? { bd: C.greenBd, bg: C.greenBg, tx: C.greenTx }
    : tone === 'sell' ? { bd: C.redBd, bg: C.redBg, tx: C.redTx }
    : { bd: C.goldBd, bg: C.goldBg, tx: C.goldDark };
  return (
    <button
      onClick={onClick}
      style={{
        padding: '9px 18px', borderRadius: 999, fontFamily: 'inherit', fontSize: 13.5, fontWeight: 400,
        cursor: 'pointer', transition: 'all .15s', whiteSpace: 'nowrap',
        border: `1.5px solid ${active ? c.bd : 'var(--jd-border)'}`,
        background: active ? c.bg : C.surface,
        color: active ? c.tx : C.muted,
      }}
    >
      {children}
    </button>
  );
}

// Status chip shown on collapsible section headers
export function StatusChip({ tone, dot, children }: { tone: 'gray' | 'gold' | 'green' | 'red'; dot?: boolean; children: React.ReactNode }) {
  const c = tone === 'green' ? { bd: C.greenBd, bg: C.greenBg, tx: C.greenTx, dotC: C.green }
    : tone === 'gold' ? { bd: C.goldBd, bg: C.goldBg, tx: C.goldDark, dotC: C.gold }
    : tone === 'red' ? { bd: C.redBd, bg: C.redBg, tx: C.redTx, dotC: C.red }
    : { bd: 'var(--jd-border)', bg: 'var(--jd-surface-3)', tx: 'var(--jd-muted)', dotC: C.placeholder };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 11.5, fontWeight: 700, padding: '4px 11px', borderRadius: 999, background: c.bg, border: `1px solid ${c.bd}`, color: c.tx }}>
      {dot && <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.dotC }} />}
      {children}
    </span>
  );
}

export function Divider({ style }: { style?: React.CSSProperties }) {
  return <div style={{ height: 1, background: C.divider, margin: '18px 0', ...style }} />;
}

// Collapsible numbered section (the gold-header accordion used throughout Step 0)
export function CollapsibleSection({
  num, title, chip, open, onToggle, children,
}: {
  num?: number;
  title: React.ReactNode;
  chip?: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div style={{ border: `1px solid ${C.borderSoft}`, borderRadius: 14, overflow: 'hidden' }}>
      <div
        onClick={onToggle}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px', cursor: 'pointer', background: 'var(--jd-surface-2)' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--jd-surface-2-hover)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--jd-surface-2)'; }}
      >
        {num !== undefined && <span style={{ width: 26, height: 26, flex: 'none', borderRadius: 8, background: 'var(--jd-badge-bg)', color: 'var(--jd-badge-tx)', fontWeight: 800, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{num}</span>}
        <span style={{ flex: 1, fontSize: 15, fontWeight: 700, color: C.title }}>{title}</span>
        {chip}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.placeholder} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform .25s', flex: 'none' }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
      {open && <div style={{ padding: '20px 18px', borderTop: `1px solid ${C.divider}` }}>{children}</div>}
    </div>
  );
}

export function JdSelect({ label, value, onChange, options, items, placeholder = '— Chọn —', style, showSearch, allowCustom }: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  options?: string[];
  items?: { value: string; label: string }[];
  placeholder?: string;
  style?: React.CSSProperties;
  showSearch?: boolean;
  allowCustom?: boolean;
}) {
  const [searchValue, setSearchValue] = React.useState('');
  const selectOptions = items ?? (options ?? []).map(o => ({ value: o, label: o }));

  // If allowCustom andsearchValue is not in options, add it
  const finalOptions = [...selectOptions];
  if (allowCustom && searchValue && !finalOptions.some(o => o.value === searchValue)) {
    finalOptions.unshift({ value: searchValue, label: `Sử dụng "${searchValue}"` });
  }

  const select = (
    <Select
      value={value || undefined}
      onChange={onChange}
      onSearch={setSearchValue}
      onBlur={() => {
        if (allowCustom && searchValue) {
          onChange(searchValue);
          setSearchValue('');
        }
      }}
      placeholder={placeholder}
      className="jd-select"
      style={{ width: '100%' }}
      classNames={{ popup: { root: 'jd-select-popup' } }}
      options={finalOptions}
      showSearch={showSearch}
      optionFilterProp="label"
    />
  );
  if (!label) return <div style={style}>{select}</div>;
  return (
    <div style={style}>
      <FieldLabel>{label}</FieldLabel>
      {select}
    </div>
  );
}

// Checklist row used in steps 1–4 + hidden prep checklist
export function CheckRow({ done, label, onClick }: { done: boolean; label: string; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '11px 12px', borderRadius: 11, cursor: 'pointer', background: done ? 'var(--jd-done-bg)' : C.surface, transition: 'background .15s' }}
      onMouseEnter={(e) => { if (!done) e.currentTarget.style.background = 'var(--jd-surface-3)'; }}
      onMouseLeave={(e) => { if (!done) e.currentTarget.style.background = C.surface; }}
    >
      <span style={{ width: 21, height: 21, flex: 'none', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: done ? C.green : C.surface, border: done ? `1px solid ${C.green}` : '1.6px solid var(--jd-border)' }}>
        {done && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 6" /></svg>}
      </span>
      <span style={{ fontSize: 13.5, fontWeight: 500, color: done ? C.onMuted : C.text }}>{label}</span>
    </div>
  );
}
